import type { Logger } from '@mimir/common/types/logger';
import { invariant } from '@mimir/common/util/invariant';

export interface TextureDataSourceTexImageSource {
  sourceType: 'texImageSource',
  texImageSource: TexImageSource,
}

export type WebGlFormatEnum = 'luminance8' | 'rg8' | 'rgb8' | 'rgba8' | 'srgb8' | 'srgb8_alpha8';

export interface TextureDataSourceBuffer {
  sourceType: 'buffer',
  format: WebGlFormatEnum,
  arrayBuffer: ArrayBufferView
}

export type TextureDataSource = TextureDataSourceBuffer | TextureDataSourceTexImageSource;

export class WebGlTextureWrapper {
  private contextId: number;
  private closed = false;
  constructor(
    private webGlWrapper: WebGlWrapper,
    readonly texture : WebGLTexture,
    readonly width: number,
    readonly height: number,
    private returnResources : () => void,
  ) {
    this.contextId = webGlWrapper.getContextId();
  }

  copyTo(target: Uint8Array): void {
    this.webGlWrapper.readTexture(this.texture, this.width, this.height, target);
  }

  close(): void {
    if (this.closed) {
      throw new Error('WebGLTextureWrapper is already closed');
    }
    this.closed = true;
    if (this.contextId === this.webGlWrapper.getContextId()) {
      this.returnResources();
    }
  }
}

export class FrameBufferWrapper {
  private contextId: number;
  private expired : boolean = false;
  constructor(
    private webGlWrapper: WebGlWrapper,
    readonly frameBuffer: WebGLFramebuffer,
    readonly width: number,
    readonly height: number,
    private colorAttachment0: WebGlTextureWrapper,
    private returnResources: () => void,
  ) {
    this.contextId = webGlWrapper.getContextId();
  }

  toTexture(): WebGlTextureWrapper {
    this.assertNotMoved();
    this.expired = true;
    return new WebGlTextureWrapper(this.webGlWrapper, this.colorAttachment0.texture, this.width, this.height, this.returnResources);
  }

  close(): void {
    this.assertNotMoved();
    this.expired = true;
    if (this.contextId === this.webGlWrapper.getContextId()) {
      this.returnResources();
    }
  }

  private assertNotMoved() {
    if (this.expired) {
      throw new Error('FrameBufferWrapper is expired. Either close() or toTexture() has been called on it');
    }
  }
}

interface UniformBlock {
  binding: number,
  arrayBuffer: ArrayBuffer,
  buffer: WebGLBuffer,
  size: number,
}

export class LinkedProgram {
  private uniformCache = new Map<string, WebGLUniformLocation | null>();
  private uniformBlocks = new Map<string, UniformBlock>();
  constructor(private program: WebGLProgram) { }

  use(gl: WebGL2RenderingContext) {
    gl.useProgram(this.program);
  }

  updateUniformBlock(gl: WebGL2RenderingContext, name: string, updateFunction: (data: ArrayBuffer) => void) {
    let block = this.uniformBlocks.get(name);
    if (block === undefined) {
      const blockIndex = gl.getUniformBlockIndex(this.program, name);
      if (blockIndex == gl.INVALID_INDEX) {
        throw new Error(`No such uniform block: ${name}`);
      }
      let binding = this.uniformBlocks.size;
      gl.uniformBlockBinding(this.program, blockIndex, binding);

      const size = gl.getActiveUniformBlockParameter(this.program, blockIndex, gl.UNIFORM_BLOCK_DATA_SIZE);

      const arrayBuffer = new ArrayBuffer(size);

      block = {
        binding,
        buffer: gl.createBuffer(),
        size,
        arrayBuffer,
      };

      gl.bindBuffer(gl.UNIFORM_BUFFER, block.buffer);
      updateFunction(block.arrayBuffer);
      gl.bufferData(gl.UNIFORM_BUFFER, block.arrayBuffer, gl.DYNAMIC_DRAW);
      this.uniformBlocks.set(name, block);

    } else {
      gl.bindBuffer(gl.UNIFORM_BUFFER, block.buffer);
      updateFunction(block.arrayBuffer);
      gl.bufferSubData(gl.UNIFORM_BUFFER, 0, block.arrayBuffer);
    }
    gl.bindBufferBase(gl.UNIFORM_BUFFER, block.binding, block.buffer);
  }

  getUniformLocation(gl: WebGL2RenderingContext, name: string): WebGLUniformLocation | null {
    const cached = this.uniformCache.get(name);
    if (cached !== undefined) {
      return cached;
    }
    const location = gl.getUniformLocation(this.program, name);
    this.uniformCache.set(name, location);
    return location;
  }

}

interface TextureCacheEntryForKey {
  /**
   *  Textures currently returned and available in the cache for the given
   *  key.
   */
  textures: WebGLTexture[],

  /**
   *  How many texture have been created in total for the given key.
   */
  count: number,
}

export class WebGlWrapper {
  private gl: WebGL2RenderingContext | null = null;

  private textureCache = new Map<string, TextureCacheEntryForKey>();
  private linkedPrograms = new Map<string, LinkedProgram>();
  private frameBuffers = new Map<string, FramebufferInternal[]>();
  private debug = false;

  private contextId : number = 0;

  constructor(
    private readonly logger: Logger,
    private readonly contextSource: OffscreenCanvas,
  ) {
    this.contextSource.addEventListener('webglcontextlost', () => {
      this.clearContext();
    });
  }

  public getCanvasResolution(): { width: number, height: number } {
    return { width: this.contextSource.width, height: this.contextSource.height }
  }

  public getContextId(): number {
    return this.contextId;
  }


  private clearContext() {
    this.gl = null;
    this.textureCache.clear();
    this.linkedPrograms.clear();
  }

  public getGlContext(): WebGL2RenderingContext {
    if (this.gl === null) {
      this.gl = this.contextSource.getContext('webgl2');
      this.contextId ++;
    }
    invariant(this.gl, 'WebGL2 context should be available');
    return this.gl;
  }

  public throwIfError(checkpointName?: string) {
    const gl = this.gl;
    invariant(gl, 'Validate first');

    if (this.debug) {
      const error = gl.getError();
      if (error === gl.NO_ERROR) {
        return;
      }
      throw new Error(`WebGL error occurred: ${getGlErrorName(gl, error)} ${checkpointName ? `(At checkpoint ${checkpointName})` : ''}`);
    }
  }

  private readFrameBuffer : WebGLFramebuffer | null = null;

  public readTexture(texture: WebGLTexture, width: number, height: number, target: Uint8Array) {
    const gl = this.getGlContext();
    if (this.readFrameBuffer === null) {
      this.readFrameBuffer = gl.createFramebuffer();
    }

    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.readFrameBuffer);
    gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);

    const requiredSize = (width * height * 4);
    if (target.length !== requiredSize) {
      throw new Error('Wrong size for destination buffer');
    }

    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, target);

    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
  }

  public getTexture(
    internalFormatEnum: WebGlFormatEnum,
    width: number,
    height: number,
    data: TextureDataSource | null
  ): WebGlTextureWrapper {
    const gl = this.getGlContext();

    const internalFormat : GLint = webGlInternalFormat(gl, internalFormatEnum);

    const key = textureKey(data?.sourceType ?? null, internalFormatEnum, width, height);

    let texture : WebGLTexture;
    let cacheEntry = this.textureCache.get(key);
    if (cacheEntry === undefined) {
      cacheEntry = { textures: [], count: 0 };
      this.textureCache.set(key, cacheEntry);
    }

    if (cacheEntry.textures.length === 0) {
      const bytesPerPixel =
        bytesPerPixelFromWebGlFormatEnum(internalFormatEnum);

      this.logger.log({
        level: 'debug',
        message: 'Creating new texture',
        width,
        height,
        internalFormatEnum,
        cacheOverview: [...this.textureCache.entries()]
          .map(([key, entry]) => {
            return {
              key,
              count: entry.count,
              memoryUsage: `${Math.round((entry.count * width * height * bytesPerPixel) / (1024 * 1024))}MB`,
            };
          })
      });

      texture = gl.createTexture();
      cacheEntry.count ++;
      gl.bindTexture(gl.TEXTURE_2D, texture);
      if (data === null) {
        gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      } else if (data.sourceType === 'texImageSource') {
        gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, gl.RGBA, gl.UNSIGNED_BYTE, data.texImageSource);
      } else if (data.sourceType === 'buffer') {
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.texImage2D(gl.TEXTURE_2D,  0, internalFormat, width, height, 0, webGlExternalFormat(gl, data.format), gl.UNSIGNED_BYTE, data.arrayBuffer);
      }
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);

      /*
        We use CLAMP_TO_EDGE to avoid wraparound artifacts along the edges of the
        rescaled image.
      */
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.bindTexture(gl.TEXTURE_2D, null);
    } else {
      texture = cacheEntry.textures.splice(0, 1)[0];
      if (data !== null) {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        switch (data.sourceType) {
          case 'texImageSource':
            /*
              Note that under normal circumstances it is more efficient to run
              texSubImage2D when re-using a texture because this does not
              allocate new internal buffers, but for TexImageSource there seems
              to be a suboptimal code in texSubImage2D which is why we use
              texImage2D even when updating an existing texture.
            */
            gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, gl.RGBA, gl.UNSIGNED_BYTE, data.texImageSource);
            break;
          case 'buffer':
            gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, width, height, webGlExternalFormat(gl, data.format), gl.UNSIGNED_BYTE, data.arrayBuffer);
            break;
        }
        gl.bindTexture(gl.TEXTURE_2D, null);
      }
    }

    return new WebGlTextureWrapper(
      this,
      texture,
      width,
      height,
      () => this.returnTexture(key, texture),
    );
  }

  returnTexture(key: string, texture: WebGLTexture) {
    const textures = this.textureCache.get(key);
    if (textures === undefined) {
      this.textureCache.set(key, { textures: [texture], count: 0 });
    } else {
      textures.textures.push(texture);
    }
  }

  public getLinkedProgram(vertexShaderSource: string, fragmentShaderSource: string): LinkedProgram {
    const key = `${vertexShaderSource}:${fragmentShaderSource}`;
    const existingProgram = this.linkedPrograms.get(key);
    if (existingProgram !== undefined) {
      return existingProgram;
    }

    const gl = this.getGlContext();
    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    invariant(vertexShader, 'Vertex shader should be created successfully');
    gl.shaderSource(vertexShader, vertexShaderSource);
    gl.compileShader(vertexShader);
    let vertexStatus = gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS);
    if (!vertexStatus) {
      const infoLog = gl.getShaderInfoLog(vertexShader);
      throw new Error(`Failed to compile vertex shader:\n${vertexShaderSource}\nInfoLog: ${infoLog}`);
    }

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    invariant(fragmentShader, 'Fragment shader should be created successfully');
    gl.shaderSource(fragmentShader, fragmentShaderSource);
    gl.compileShader(fragmentShader);
    let fragmentStatus = gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS);
    if (!fragmentStatus) {
      const infoLog = gl.getShaderInfoLog(fragmentShader);
      throw new Error(`Failed to compile fragment shader:\n${fragmentShaderSource}\nInfoLog: ${infoLog}`);
    }

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    const linkStatus = gl.getProgramParameter(program, gl.LINK_STATUS);
    if (!linkStatus) {
      const linkInfo = gl.getProgramInfoLog(program);
      throw new Error(`Program linking failed: ${linkInfo}`);
    }

    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    const linkedProgram = new LinkedProgram(program);
    this.linkedPrograms.set(key, linkedProgram);
    return linkedProgram;
  }

  /**
   *  WARNING: This call WILL mess with the bound textures upon being created.
   *  Because of this you should always obtain the frame buffer before you
   *  start binding texture for a draw call
   */
  public getFrameBuffer(width: number, height: number) {
    const key = frameBufferKey(width, height);

    let frameBufferInternal : FramebufferInternal;

    const frameBuffers = this.frameBuffers.get(key);

    if ((frameBuffers === undefined) || (frameBuffers.length === 0)) {
      this.logger.log({
        level: 'debug',
        message: 'Creating new frame buffer',
        width,
        height,
      });
      const gl = this.getGlContext();
      const frameBuffer = gl.createFramebuffer();
      // Since we don't want to lose color resolution in darker areas between
      // shader passes we want the render result to be stored as sRGB.
      const textureWrapper = this.getTexture('srgb8_alpha8', width, height, null);
      gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, textureWrapper.texture, 0);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);

      frameBufferInternal = {
        frameBuffer,
        colorAttachment0: textureWrapper,
      };
    } else {
      frameBufferInternal = frameBuffers.splice(0, 1)[0];
    }

    return new FrameBufferWrapper(
      this,
      frameBufferInternal.frameBuffer,
      width,
      height,
      frameBufferInternal.colorAttachment0,
      () => {
        this.returnFrameBuffer(width, height, frameBufferInternal);
      }
    );
  }

  public returnFrameBuffer(width: number, height: number, frameBufferInternal: FramebufferInternal) {
    const key = frameBufferKey(width, height);
    const frameBuffers = this.frameBuffers.get(key);
    if (frameBuffers === undefined) {
      this.frameBuffers.set(key, [frameBufferInternal]);
    } else {
      frameBuffers.push(frameBufferInternal);
    }
  }
}

function webGlInternalFormat(gl: WebGL2RenderingContext, e: WebGlFormatEnum): GLint {
  switch (e) {
    case 'rgba8': return gl.RGBA8;
    case 'rgb8': return gl.RGB8;
    case 'srgb8_alpha8': return gl.SRGB8_ALPHA8;
    case 'srgb8': return gl.SRGB8;
    case 'luminance8': return gl.LUMINANCE;
    case 'rg8': return gl.RG8;
  }
}

function bytesPerPixelFromWebGlFormatEnum(e: WebGlFormatEnum): number {
  switch (e) {
    case 'rgba8': return 4;
    case 'rgb8': return 3;
    case 'srgb8_alpha8': return 4;
    case 'srgb8': return 3;
    case 'luminance8': return 1;
    case 'rg8': return 2;
  }
}

function webGlExternalFormat(gl: WebGL2RenderingContext, e: WebGlFormatEnum): GLint {
  switch (e) {
    case 'rgba8':
    case 'srgb8_alpha8':
      return gl.RGBA;
    case 'rgb8':
    case 'srgb8':
      return gl.RGB;
    case 'rg8': return gl.RG;
    case 'luminance8': return gl.LUMINANCE;
  }
}
function textureKey(
  textureSourceType: 'texImageSource' | 'buffer' | null,
  format: WebGlFormatEnum,
  width: GLint,
  height: GLint
) {
  return `${textureSourceType}:${format}:${width}:${height}`;
}

function getGlErrorName(gl: WebGL2RenderingContext, error: number): string {
  switch (error) {
    case gl.INVALID_OPERATION:
      return 'INVALID_OPERATION';
    case gl.OUT_OF_MEMORY:
      return 'OUT_OF_MEMORY';
  }
  return '0x' + error.toString(16).toUpperCase().padStart(4, '0');
}

function frameBufferKey(width: number, height: number): string {
  return `${width}:${height}`;
}

interface FramebufferInternal {
  frameBuffer: WebGLFramebuffer,
  colorAttachment0: WebGlTextureWrapper,
}

